import { switchMap } from 'rxjs/operators';
import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NavigationService } from '../../../core/service/navigation.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { of, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { LocalDate } from '../../../core/model/local-date';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from '../../../core/service/message.service';
import { LeaseAgreementService } from '../../service/data/lease-agreement.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { DocumentTypeMimeTypes } from '../../../core/model/const/document-type-mime-types';
import {
  ESignatory,
  InformationChannelKind,
  SignatureReminderSettingKind,
  SignatureTypeKind,
  SigningRoleKind,
} from '../../model/esignatory';
import { RentalObjectCategory } from '../../model/const/enums';
import { configureESignatoryContactValidation } from '../../helper/esignatory-form-validation';

@Component({
    selector: 'app-esign-lease-agreement-document',
    templateUrl: './esign-lease-agreement-document.component.html',
    styleUrls: ['./esign-lease-agreement-document.component.scss'],
    providers: [NavigationService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ESignLeaseAgreementDocumentComponent implements OnDestroy, OnInit {
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _fc: FormControlExFactory,
    private _navigation: NavigationService,
    private _nativeWindow: NativeWindowService,
    private _leaseAgreementService: LeaseAgreementService,
    private _fb: FormBuilder,
    private _messageService: MessageService
  ) {
    this._subscriptions = new Subscription();

    //this.expirationDate = LocalDate.fromDate(LocalDate.today().addDays(7));

    this.form = this._fb.group({
      expirationDate: this._fc.createMandatory(
        'ESIGNATURE.EXPIRATION_DATE',
        null,
        'ESIGNATURE.HELP_TEXT.EXPIRATION_DATE'
      ),
      formName: this._fc.createMandatory('ESIGNATURE.DOCUMENT', ''),
      errandDescription: this._fc.createMandatory('ESIGNATURE.DESCRIPTION', ''),
      approveAgreementAfterSigning: this._fc.create('ESIGNATURE.APPROVE_AGREEMENT_AFTER_SIGNING', 1),
      informWhenSigned: this._fc.create('ESIGNATURE.INFORM_WHEN_SIGNED', 1),
      informEmailAddress: this._fc.create('ESIGNATURE.INFORM_EMAIL_ADRESSES', ''),
      signatories: this._fb.array([]),
      attachments: this._fb.array([]),
    });
  }

  form: FormGroup;
  expirationDate: LocalDate;
  leaseAgreementId: string;
  leaseAgreementNumber: string;
  category: RentalObjectCategory;
  formsForEsign = [];
  mimeTypes: string[] = DocumentTypeMimeTypes.PDF();

  civicNumberMask = /^(\d{8}\-\d{0,4})|(\d{1,8})/;
  readonly civicNumberMaskFormat: string = 'ÅÅÅÅMMDD-NNNN';

  signatureTypeSelection = [
    new SelectionItem('ESIGNATURE.SIGNATURE_TYPE.BANKID', SignatureTypeKind.BankId),
    new SelectionItem('ESIGNATURE.SIGNATURE_TYPE.CLICK', SignatureTypeKind.Click),
  ];

  informationChannels = [
    new SelectionItem('ESIGNATURE.INFORMATION_CHANNEL.EMAIL', InformationChannelKind.Email),
    new SelectionItem('ESIGNATURE.INFORMATION_CHANNEL.SMS', InformationChannelKind.Sms),
    new SelectionItem('ESIGNATURE.INFORMATION_CHANNEL.EMAIL_AND_SMS', InformationChannelKind.EmailAndSms),
  ];

  signingRoles = [
    new SelectionItem('ESIGNATURE.SIGNING_ROLE.NOT_SPECIFIED', SigningRoleKind.NotSpecified),
    new SelectionItem('ESIGNATURE.SIGNING_ROLE.COMPANY_REPRESENTATIVE', SigningRoleKind.CompanyRepresentative),
    new SelectionItem('ESIGNATURE.SIGNING_ROLE.TENANT', SigningRoleKind.Tenant),
    new SelectionItem('LEASE_AGREEMENT.CREDITOR', SigningRoleKind.Guarantor),
  ];

  signatureReminderSettingSelection = [
    new SelectionItem('ESIGNATURE.SIGNATURE_REMINDER_SETTING.NO_REMINDERS', SignatureReminderSettingKind.NoReminders),
    new SelectionItem('ESIGNATURE.SIGNATURE_REMINDER_SETTING.ONCE_A_DAY', SignatureReminderSettingKind.OnceADay),
  ];

  private addSignatory(signatory: ESignatory) {
    const signatories = this.form.controls['signatories'] as FormArray;
    const signatoryForm = this._fb.group({
        id: this._fc.createHidden(signatory.id),
        civicNumber: this._fc.create('ESIGNATURE.SIGNATORY.CIVIC_NUMBER', signatory.civicNumber),
        email: this._fc.createEmail('ESIGNATURE.SIGNATORY.EMAIL', signatory.email),
        informationChannel: this._fc.createMandatory('ESIGNATURE.SIGNATORY.INFORMATION_CHANNEL', signatory.informationChannel),
        signatureType: this._fc.createMandatory('ESIGNATURE.SIGNATORY.SIGNATURE_TYPE', signatory.signatureType),
        name: this._fc.createMandatory('ESIGNATURE.SIGNATORY.NAME', signatory.name),
        phone: this._fc.create('ESIGNATURE.SIGNATORY.PHONE', signatory.phone),
        signOrder: this._fc.create('ESIGNATURE.SIGNATORY.SIGN_ORDER', signatory.signOrder),
        signingRole: this._fc.createMandatory('COMMON.ROLE', signatory.signingRole),
        signatureReminderSetting: this._fc.createMandatory(
          'ESIGNATURE.SIGNATORY.SIGNATURE_REMINDER_SETTING',
          signatory.signatureReminderSetting
        ),
      });
    signatories.push(signatoryForm);
    this.AddSubscription(configureESignatoryContactValidation(signatoryForm));
  }

  ngOnInit() {
    this.AddSubscription(
      this._activatedRoute.paramMap
        .pipe(
          switchMap(map => {
            let id = map.get('leaseAgreementId');
            if (id) {
              this.leaseAgreementId = id;
              return this._leaseAgreementService.getDataForESign(id);
            }
            return of([]);
          })
        )
        .subscribe(data => {
          this._setEsignData(data);
        })
    );
  }

  private _setEsignData(context: any) {
    if (context) {
      this.leaseAgreementNumber = context.leaseAgreementNumber;
      this.category = context.category;
      this.expirationDate = LocalDate.fromDate(LocalDate.today().addDays(context.defaultResponseTime));
      this.form.controls['expirationDate'].setValue(this.expirationDate);

      this.form.controls['errandDescription'].setValue(context.errandDescription);
      switch (this.category) {
        case RentalObjectCategory.Dwelling: {
          this.formsForEsign = [{ name: 'LEASE_AGREEMENT.DWELLING_PRINTED_FORM_NAME', value: 'DwellingLeaseAgreement' }];
          this.form.controls['formName'].setValue('DwellingLeaseAgreement');
          break;
        }
        case RentalObjectCategory.Parking: {
          this.formsForEsign = [{ name: 'LEASE_AGREEMENT.PARKING_PRINTED_FORM_NAME', value: 'ParkingLeaseAgreement' }];
          this.form.controls['formName'].setValue('ParkingLeaseAgreement');
          break;
        }
        case RentalObjectCategory.Premises: {
          this.formsForEsign = [{ name: 'PREMISES_LEASE_AGREEMENT_FORM.PREMISES_LABEL', value: 'PremisesLeaseAgreement' }];
          this.form.controls['formName'].setValue('PremisesLeaseAgreement');
          break;
        }
        case RentalObjectCategory.Other: {
          break;
        }
      }

      this.form.controls['informEmailAddress'].setValue(context.informEmailAddress);

      for (const signatory of context.signatories) {
        this.addSignatory(signatory);
      }

      let signatory = new ESignatory();
      signatory.civicNumber = context.currentUserCivicNumber;
      signatory.name = context.currentUserPersonName;
      signatory.email = context.currentUserEmailAddress;
      signatory.phone = context.currentUserPhone;
      signatory.informationChannel = context.currentUserInformationChannel;
      signatory.signingRole = context.currentUserSigningRole;
      signatory.signOrder = context.currentUserSignOrder;
      signatory.signatureType = context.currentUserSignatureType;
      signatory.signatureReminderSetting = context.currentUserSignatureReminderSetting;
      this.addSignatory(signatory);

      this.form.markAllAsTouched();
    }
  }

  get signatoryForms(): FormGroup[] {
    return (this.form.controls['signatories'] as FormArray).controls.map(c => c as FormGroup);
  }

  newSignatory() {
    this.addSignatory(new ESignatory());
  }

  deleteSignatory(index: number) {
    const signatories = this.form.controls['signatories'] as FormArray;
    signatories.removeAt(index);
  }

  private readonly _subscriptions: Subscription;
  private _resultSub: Subscription;

  protected AddSubscription(subscription: Subscription): void {
    if (!subscription) {
      throw new Error('Null reference. Object not set.');
    }

    this._subscriptions.add(subscription);
  }

  private clearSubscriptions() {
    if (this._resultSub && !this._resultSub.closed) this._resultSub.unsubscribe();
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
    this.clearSubscriptions();
  }

  cancel() {
    this._navigation.navigateBack();
  }

  previewForm() {
    switch (this.form.controls['formName'].value) {
      case 'DwellingLeaseAgreement': {
        let serviceResult = this._leaseAgreementService.getDwellingLeaseAgreementForm(this.leaseAgreementId);
        this.AddSubscription(
          serviceResult.subscribe(res => {
            this._nativeWindow.openFile(res, `${this.leaseAgreementNumber}.pdf`);
          })
        );
        break;
      }

      case 'ParkingLeaseAgreement': {
        let serviceResult = this._leaseAgreementService.getParkingLeaseAgreementForm(this.leaseAgreementId);
        this.AddSubscription(
          serviceResult.subscribe(res => {
            this._nativeWindow.openFile(res, `${this.leaseAgreementNumber}.pdf`);
          })
        );
        break;
      }

      case 'PremisesLeaseAgreement': {
        let serviceResult = this._leaseAgreementService.getPremisesLeaseAgreementForm(this.leaseAgreementId);
        this.AddSubscription(
          serviceResult.subscribe(res => {
            this._nativeWindow.openFile(res, `${this.leaseAgreementNumber}.pdf`);
          })
        );
        break;
      }
    }
  }

  save() {
    if (this.form.disabled) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    let context = this.form.value;
    context.leaseAgreementId = this.leaseAgreementId;

    context.attachments = (this.form.controls['attachments'] as FormArray).controls.map(f => f.value.data);
    this.form.disable();

    this.AddSubscription(
      this._leaseAgreementService.sendForEsign(context).subscribe(
        () => this._navigation.navigateBack(),
        error => {
          this.form.enable();
        }
      )
    );
  }
}
